import { clear, el, toast } from '../core/dom.js';
import { getState, planFor } from '../core/state.js';
import { CATEGORY_TO_DOMAIN } from '../domain/default-methods.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { events as eventsStore, logEvent, planStates, rescueStats, markEventUndone } from '../storage/db.js';
import { applyUsage as intervalApply } from '../rules/interval-engine.js';
import { applyUsage as quotaApply } from '../rules/quota-engine.js';
import { applyUsage as windowApply } from '../rules/window-engine.js';
import {
  closeEpisode, elapsedMs, getEpisode, linkEventToEpisode, updateEpisode,
} from '../episodes/episode-store.js';
import { showUndoToast } from '../components/undo-toast.js';
import { createTimerStatusRing } from '../components/timer-status-ring.js';
import { copy } from '../copy/tone.js';
import { recommend } from '../rescue/rescue-engine.js';
import { getTemplate } from '../domain/templates.js';
import { runRescueModule } from '../rescue/run-rescue-module.js';
import { buildBrowseAllSection } from './rescue-screen.js';
import { generateChallenge, evaluateChallenge } from '../rescue/challenge-engine.js';
import type { GeneratedChallenge } from '../rescue/challenge-types.js';
import { navigate, currentRoute } from '../router.js';
import { track as gaTrack } from '../analytics/ga.js';

/**
 * Fire an explicit urge-resolution event with a derived deferral_kind enum.
 * Makes "rescue deferred urge" queryable in GA without joining events.
 *
 *   passed + modulesCompleted>0 -> 'deferred_with_rescue'
 *   passed + modulesCompleted=0 -> 'deferred_without_rescue'
 *   used   + modulesCompleted>0 -> 'used_after_rescue'
 *   used   + modulesCompleted=0 -> 'used_no_rescue'
 */
function trackUrgeResolution(
  outcome: 'passed' | 'used',
  modulesCompleted: number,
  totalWaitedMs: number,
): void {
  const ran = modulesCompleted > 0;
  const deferral_kind =
    outcome === 'passed'
      ? (ran ? 'deferred_with_rescue' : 'deferred_without_rescue')
      : (ran ? 'used_after_rescue' : 'used_no_rescue');
  gaTrack('urge_resolved', {
    outcome,
    deferral_kind,
    modules_completed: modulesCompleted,
    waited_ms: Math.round(totalWaitedMs),
  });
}
import type {
  BehaviorDomain, ChallengeDifficulty, EpisodeRecord, HabitRecord, PlanStateRecord,
} from '../core/types.js';

let timerHandle: number | null = null;
let activeChallenge: GeneratedChallenge | null = null;
let activeHashListener: (() => void) | null = null;

function stopTimerHandle(h: number): void {
  if (h < 0) cancelAnimationFrame(-h);
  else window.clearInterval(h);
}

export async function renderUrgeCommandCenter(parent: HTMLElement): Promise<void> {
  clear(parent);
  if (timerHandle !== null) { stopTimerHandle(timerHandle); timerHandle = null; }
  if (activeHashListener) {
    window.removeEventListener('hashchange', activeHashListener);
    activeHashListener = null;
  }

  const episodeId = extractEpisodeId();
  const ep = episodeId ? await getEpisode(episodeId) : undefined;
  if (!ep) {
    parent.appendChild(el('div', { class: 'card center' },
      el('h2', {}, 'No active urge'),
      el('p', { class: 'muted' }, 'This urge episode is no longer active.'),
      el('button', { class: 'btn primary block', onclick: () => navigate('/today') }, 'Back to today'),
    ));
    return;
  }
  if (ep.endedAt) {
    parent.appendChild(el('div', { class: 'card center' },
      el('h2', {}, 'Urge closed'),
      el('p', { class: 'muted' }, `Outcome: ${ep.outcome ?? 'unknown'}.`),
      el('button', { class: 'btn primary block', onclick: () => navigate('/today') }, 'Back to today'),
    ));
    return;
  }

  const st = getState();
  const habit = st.habits.find((h) => h.id === ep.habitId);
  if (!habit) {
    parent.appendChild(el('p', {}, 'Habit not found.'));
    return;
  }
  const domain: BehaviorDomain = habit.domain ?? CATEGORY_TO_DOMAIN[habit.category];
  const reducedMotion = st.settings.reducedMotion === 'on'
    || (st.settings.reducedMotion === 'auto' && matchMedia('(prefers-reduced-motion: reduce)').matches);

  const root = el('div', { class: 'urge-modal' });
  parent.appendChild(root);

  const epId = ep.id;
  function openCloseSheet(): void {
    const existing = root.querySelector('[data-role="close-sheet"]');
    if (existing) { existing.remove(); return; }
    const sheet = el('div', { class: 'card', dataset: { role: 'close-sheet' },
      attrs: { style: 'border-color:var(--accent-soft);' } },
      el('div', { class: 'eyebrow' }, 'Close urge support?'),
      el('p', { attrs: { style: 'margin:0 0 0.5rem;' } }, 'What happened?'),
      el('div', { class: 'stack' },
        el('button', {
          class: 'btn primary block', attrs: { type: 'button' },
          onclick: () => { sheet.remove(); },
        }, 'Keep it open'),
        el('button', {
          class: 'btn block', attrs: { type: 'button' },
          onclick: async () => { sheet.remove(); await onUrgeBeaten(); },
        }, "I've beaten this urge"),
        el('button', {
          class: 'btn block', attrs: { type: 'button' },
          onclick: async () => { sheet.remove(); await onLogUsed(); },
        }, 'I used'),
        el('button', {
          class: 'btn ghost block', attrs: { type: 'button' },
          onclick: async () => {
            sheet.remove();
            await closeEpisode(epId, 'abandoned');
            navigate('/today');
          },
        }, "I'm stepping away — close for now"),
      ),
    );
    root.appendChild(sheet);
  }

  // Header
  const head = el('div', { class: 'urge-header' },
    el('h2', {}, copy('urge_logged', st.settings.tone)),
    el('p', { class: 'muted', attrs: { style: 'margin:var(--space-2) 0 0;' } },
      `${habit.privateLabel ?? habit.label} · urges fade — buy a few minutes.`),
    el('button', {
      class: 'btn ghost', attrs: { type: 'button', style: 'margin-top:0.5rem;' },
      onclick: () => openCloseSheet(),
    }, 'Close support'),
  );
  root.appendChild(head);

  // Timer
  const timer = createTimerStatusRing({
    startedAt: ep.startedAt,
    ...(ep.targetWaitMs !== undefined ? { targetWaitMs: ep.targetWaitMs } : {}),
    reducedMotion,
  });
  const timerCard = el('div', { class: 'card', attrs: { style: 'display:flex;justify-content:center;' } }, timer.root);
  root.appendChild(timerCard);
  // Reduced-motion: a calm interval (5s) is plenty. Otherwise rAF for the
  // hundredths display per doc 20. timerHandle stores the latest raf id (as
  // a negative sentinel) so stopTimerHandle cancels the in-flight frame.
  if (reducedMotion) {
    timerHandle = window.setInterval(() => timer.tick(), 5000);
  } else {
    const loop = (): void => {
      timer.tick();
      timerHandle = -requestAnimationFrame(loop);
    };
    timerHandle = -requestAnimationFrame(loop);
  }
  // Stop the timer when the user leaves /urge/ via any route change.
  const onHashChange = (): void => {
    if (!location.hash.startsWith('#/urge/')) {
      if (timerHandle !== null) { stopTimerHandle(timerHandle); timerHandle = null; }
      window.removeEventListener('hashchange', onHashChange);
      if (activeHashListener === onHashChange) activeHashListener = null;
    }
  };
  activeHashListener = onHashChange;
  window.addEventListener('hashchange', onHashChange);

  root.appendChild(buildIntensityStrip(ep));
  root.appendChild(buildTriggerChipStrip(ep, habit));

  // Body container - dynamic state
  const body = el('div');
  root.appendChild(body);

  const renderModuleList = async (): Promise<void> => {
    clear(body);
    const stats = await rescueStats.byHabit(habit.id);
    const successByModuleId: Record<string, number> = {};
    for (const s of stats) successByModuleId[s.moduleId] = s.starts > 0 ? s.completions / s.starts : 0;
    const recent = [...stats].sort((a, b) => (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? ''))
      .slice(0, 2).map((s) => s.moduleId);
    const list = recommend({
      preferredKinds: st.settings.preferredRescueTypes,
      reducedMotion,
      durationSec: st.settings.defaultRescueDurationSec,
      ...(st.settings.triggers ? { triggers: st.settings.triggers } : {}),
      recentlyUsedModuleIds: recent,
      successByModuleId,
      domain,
      ...(st.settings.favoriteRescueModuleIds ? { favoriteModuleIds: st.settings.favoriteRescueModuleIds } : {}),
      limit: 4,
    });

    body.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Buy a few minutes'),
      el('p', { class: 'muted' }, 'Pick something. Return here when done.'),
      ...list.map((m) => el('button', {
        class: 'btn block',
        attrs: { type: 'button', style: 'margin-top:0.5rem;text-align:left;' },
        onclick: () => onStartModule(m.id),
      }, m.name)),
    ));

    body.appendChild(buildBrowseAllSection(
      new Set(list.map((m) => m.id)),
      domain,
      reducedMotion,
      (moduleId) => onStartModule(moduleId),
    ));

    body.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Or close the urge'),
      el('div', { class: 'stack' },
        el('button', {
          class: 'btn primary block',
          attrs: { type: 'button' },
          onclick: () => onUrgeBeaten(),
        }, "I've beaten this urge"),
        el('button', {
          class: 'btn block',
          attrs: { type: 'button' },
          onclick: () => onChallenge(ep.rescueSessionIds?.length ? 'stretch' : 'steady'),
        }, "I don't think I can wait"),
        el('button', {
          class: 'btn ghost block',
          attrs: { type: 'button' },
          onclick: () => onLogUsed(),
        }, 'Log my use'),
      ),
    ));
  };

  const onStartModule = async (moduleId: string, challenge?: GeneratedChallenge): Promise<void> => {
    if (timerHandle !== null) { stopTimerHandle(timerHandle); timerHandle = null; }
    clear(parent);
    const host = el('div');
    parent.appendChild(host);
    const fresh = await getEpisode(ep.id);
    if (!fresh) { navigate('/today'); return; }
    const result = await runRescueModule(host, {
      moduleId,
      habitId: habit.id,
      ...(st.activePlan?.id ? { planId: st.activePlan.id } : {}),
      reducedMotion,
      ...(st.settings.motivationAnchor ? { motivation: st.settings.motivationAnchor } : {}),
      episodeId: ep.id,
      ...(challenge ? { challenge } : {}),
    });
    if (challenge) {
      const ev = evaluateChallenge(challenge, result.details, result.result === 'completed', result.durationMs);
      const nowD = new Date();
      await logEvent({
        type: 'urge_challenge_resolved',
        habitId: habit.id,
        ...(st.activePlan?.id ? { planId: st.activePlan.id } : {}),
        episodeId: ep.id,
        occurredAt: nowD.toISOString(),
        localDate: localDate(nowD),
        timezoneOffsetMinutes: tzOffsetMinutes(nowD),
        payload: {
          episodeId: ep.id,
          challengeId: challenge.id,
          moduleId: challenge.moduleId,
          metric: challenge.metric,
          target: challenge.target,
          difficulty: challenge.difficulty,
          result: ev.result,
          ...(ev.actual !== undefined ? { actual: ev.actual } : {}),
          rescueSessionId: result.sessionId,
        },
      });
      activeChallenge = null;
    }
    // Return to the command center — but only if the user is still on the
    // urge route. If they navigated away during the module, don't clobber
    // whatever they're now viewing. Use navigate(samePath) so the router's
    // synthetic hashchange forces app-shell to rebuild `main` with a fresh
    // reference; rendering into the captured `parent` directly was fragile
    // because the shell could rebuild during a long module run.
    if (location.hash.startsWith('#/urge/')) {
      navigate(`/urge/${ep.id}`);
    }
  };

  const onChallenge = async (difficulty: ChallengeDifficulty): Promise<void> => {
    const fresh = await getEpisode(ep.id);
    // rescueSessionIds are UUIDs, not module ids. Pull module ids out of the
    // rescue_started events linked to this episode so challenge generation
    // and difficulty escalation see real module fingerprints.
    const recentModuleIds: string[] = [];
    if (fresh) {
      const habitEvents = await eventsStore.byHabit(habit.id);
      for (const e of habitEvents) {
        if (e.type !== 'rescue_started') continue;
        if (e.episodeId !== ep.id) continue;
        const mid = (e.payload as { moduleId?: unknown }).moduleId;
        if (typeof mid === 'string') recentModuleIds.push(mid);
      }
    }
    const ch = generateChallenge({
      habitId: habit.id, domain, reducedMotion,
      recentModuleIds,
      difficulty,
      excludeTemplateIds: activeChallenge ? [activeChallenge.template.id] : [],
    });
    if (!ch) { toast('No safe challenge available.'); return; }
    activeChallenge = ch;
    const nowD = new Date();
    const evRec = await logEvent({
      type: 'urge_challenge_generated',
      habitId: habit.id,
      ...(st.activePlan?.id ? { planId: st.activePlan.id } : {}),
      episodeId: ep.id,
      occurredAt: nowD.toISOString(),
      localDate: localDate(nowD),
      timezoneOffsetMinutes: tzOffsetMinutes(nowD),
      payload: {
        episodeId: ep.id, challengeId: ch.id, moduleId: ch.moduleId,
        metric: ch.metric, target: ch.target, difficulty: ch.difficulty,
        source: 'still_strong_button',
        ...(ch.template.safetyTags ? { safetyTags: ch.template.safetyTags } : {}),
      },
    });
    await linkEventToEpisode(ep.id, evRec.id);
    await updateEpisode(ep.id, { currentChallengeId: ch.id });
    renderChallenge(ch);
  };

  const renderChallenge = (ch: GeneratedChallenge): void => {
    clear(body);
    body.appendChild(el('div', { class: 'card challenge-card' },
      el('h2', {}, copy('challenge_offer', st.settings.tone)),
      el('p', {}, ch.prompt),
      el('p', { class: 'muted', attrs: { style: 'font-size:0.85rem;' } },
        `Difficulty: ${ch.difficulty.replace('_', ' ')}.`),
      el('div', { class: 'stack' },
        el('button', {
          class: 'btn primary block',
          attrs: { type: 'button' },
          onclick: async () => {
            const nowD = new Date();
            await logEvent({
              type: 'urge_challenge_accepted',
              habitId: habit.id,
              ...(st.activePlan?.id ? { planId: st.activePlan.id } : {}),
              episodeId: ep.id,
              occurredAt: nowD.toISOString(),
              localDate: localDate(nowD),
              timezoneOffsetMinutes: tzOffsetMinutes(nowD),
              payload: { episodeId: ep.id, challengeId: ch.id, moduleId: ch.moduleId },
            });
            await onStartModule(ch.moduleId, ch);
          },
        }, 'Start challenge'),
        el('button', {
          class: 'btn block',
          attrs: { type: 'button' },
          onclick: async () => {
            const nowD = new Date();
            await logEvent({
              type: 'urge_challenge_regenerated',
              habitId: habit.id,
              ...(st.activePlan?.id ? { planId: st.activePlan.id } : {}),
              episodeId: ep.id,
              occurredAt: nowD.toISOString(),
              localDate: localDate(nowD),
              timezoneOffsetMinutes: tzOffsetMinutes(nowD),
              payload: { episodeId: ep.id, fromChallengeId: ch.id },
            });
            await onChallenge(ch.difficulty);
          },
        }, 'Try a different one'),
        el('button', {
          class: 'btn ghost block',
          attrs: { type: 'button' },
          onclick: () => onLogUsed(),
        }, 'Log my use instead'),
      ),
    ));
  };

  const stopTimer = (): void => {
    if (timerHandle !== null) { stopTimerHandle(timerHandle); timerHandle = null; }
  };

  const onUrgeBeaten = async (): Promise<void> => {
    stopTimer();
    await markUrgeBeaten(ep, habit);
    navigate('/today');
  };

  const onLogUsed = async (): Promise<void> => {
    stopTimer();
    await logUsedFromUrge(ep, habit);
    navigate('/today');
  };

  await renderModuleList();
}

const INTENSITY_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'low', 2: 'mild', 3: 'medium', 4: 'high', 5: 'peak',
};

function buildIntensityStrip(ep: EpisodeRecord): HTMLElement {
  const startSet = ep.intensityStart !== undefined;
  const card = el('div', { class: 'card', attrs: { 'aria-label': 'Urge intensity' } });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, startSet ? 'Where is it now?' : 'How strong does this feel?'));
  const row = el('div', { class: 'row', attrs: { style: 'gap:0.4rem;flex-wrap:wrap;' } });
  let selected: 1 | 2 | 3 | 4 | 5 | undefined = startSet ? ep.intensityEnd : ep.intensityStart;
  const refresh = (): void => {
    for (const child of [...row.children]) {
      const lvl = Number((child as HTMLElement).dataset['lvl']) as 1 | 2 | 3 | 4 | 5;
      const isActive = selected === lvl;
      (child as HTMLElement).style.borderColor = isActive ? 'var(--accent)' : '';
      child.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  };
  for (const lvl of [1, 2, 3, 4, 5] as const) {
    const btn = el('button', {
      class: 'tag', attrs: { type: 'button', 'aria-pressed': 'false' },
    }, `${lvl} · ${INTENSITY_LABELS[lvl]}`);
    btn.dataset['lvl'] = String(lvl);
    btn.onclick = async () => {
      selected = lvl;
      refresh();
      if (!startSet) {
        await updateEpisode(ep.id, { intensityStart: lvl });
        ep.intensityStart = lvl;
      } else {
        await updateEpisode(ep.id, { intensityEnd: lvl });
        ep.intensityEnd = lvl;
      }
    };
    row.appendChild(btn);
  }
  card.appendChild(row);
  refresh();
  return card;
}

function buildTriggerChipStrip(ep: EpisodeRecord, habit: HabitRecord): HTMLElement {
  const template = getTemplate(habit.category);
  const settingsTriggers = getState().settings.triggers ?? [];
  const merged = Array.from(new Set([
    ...(ep.triggers ?? []),
    ...settingsTriggers,
    ...(template?.commonTriggers ?? []),
  ]));
  if (merged.length === 0) return el('div');
  const selected = new Set(ep.triggers ?? []);
  const card = el('div', { class: 'card', attrs: { 'aria-label': 'Triggers' } });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Triggers'));
  card.appendChild(el('p', {
    class: 'muted',
    attrs: { style: 'margin:0 0 0.5rem;font-size:0.85rem;' },
  }, 'Tap any that fit. Saved with this urge.'));
  const row = el('div', { class: 'kbd-list', attrs: { style: 'flex-wrap:wrap;gap:0.4rem;' } });
  for (const tag of merged) {
    const btn = el('button', {
      class: 'tag', attrs: { type: 'button', 'aria-pressed': selected.has(tag) ? 'true' : 'false' },
    }, tag.replace(/_/g, ' '));
    if (selected.has(tag)) btn.style.borderColor = 'var(--accent)';
    btn.onclick = async () => {
      if (selected.has(tag)) { selected.delete(tag); btn.style.borderColor = ''; btn.setAttribute('aria-pressed', 'false'); }
      else { selected.add(tag); btn.style.borderColor = 'var(--accent)'; btn.setAttribute('aria-pressed', 'true'); }
      await updateEpisode(ep.id, { triggers: [...selected] });
    };
    row.appendChild(btn);
  }
  card.appendChild(row);
  return card;
}

async function markUrgeBeaten(ep: EpisodeRecord, habit: HabitRecord): Promise<void> {
  const st = getState();
  const { plan } = planFor(habit.id, st);
  // Refresh in case the user toggled triggers after the modal first rendered.
  const fresh = (await getEpisode(ep.id)) ?? ep;
  const elapsed = elapsedMs(fresh);
  const nowD = new Date();
  const challengeMet = fresh.currentChallengeId !== undefined;
  const triggers = fresh.triggers && fresh.triggers.length > 0 ? fresh.triggers : undefined;
  const ev = await logEvent({
    type: 'success_logged',
    habitId: habit.id,
    ...(plan ? { planId: plan.id } : {}),
    episodeId: fresh.id,
    occurredAt: nowD.toISOString(),
    localDate: localDate(nowD),
    timezoneOffsetMinutes: tzOffsetMinutes(nowD),
    payload: {
      kind: 'urge_passed',
      episodeId: fresh.id,
      ...(fresh.currentChallengeId ? { challengeId: fresh.currentChallengeId } : {}),
      waitedMs: elapsed,
      ...(fresh.targetWaitMs !== undefined ? { targetWaitMs: fresh.targetWaitMs } : {}),
      modulesCompleted: fresh.modulesCompleted ?? 0,
      challengeMet,
      ...(triggers ? { triggers } : {}),
      ...(fresh.intensityStart !== undefined ? { intensityStart: fresh.intensityStart } : {}),
      ...(fresh.intensityEnd !== undefined ? { intensityEnd: fresh.intensityEnd } : {}),
      ...(fresh.intensityStart !== undefined && fresh.intensityEnd !== undefined
        ? { intensityChange: fresh.intensityEnd - fresh.intensityStart } : {}),
    },
  });
  await linkEventToEpisode(ep.id, ev.id);
  await closeEpisode(ep.id, 'passed');
  await logEvent({
    type: 'urge_episode_closed',
    habitId: habit.id,
    ...(plan ? { planId: plan.id } : {}),
    episodeId: ep.id,
    occurredAt: nowIso(),
    localDate: localDate(new Date()),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date()),
    payload: {
      episodeId: ep.id, outcome: 'passed',
      totalWaitedMs: elapsed, modulesCompleted: ep.modulesCompleted ?? 0,
      challengeMet,
    },
  });
  trackUrgeResolution('passed', ep.modulesCompleted ?? 0, elapsed);
  showUndoToast({
    message: copy('urge_beaten', st.settings.tone),
    onUndo: async () => {
      await markEventUndone(ev.id, 'user_undo');
      await updateEpisode(ep.id, { endedAt: undefined, outcome: undefined });
      toast('Reopened.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    },
  });
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

async function logUsedFromUrge(ep: EpisodeRecord, habit: HabitRecord): Promise<void> {
  const st = getState();
  const { plan, planState } = planFor(habit.id, st);
  const fresh = (await getEpisode(ep.id)) ?? ep;
  const elapsed = elapsedMs(fresh);
  const nowD = new Date();
  const triggers = fresh.triggers && fresh.triggers.length > 0 ? fresh.triggers : undefined;

  // Run the plan-engine pipeline so this counts as a real use against the
  // active plan: interval lastUseAt advances, quota usedThisPeriod ticks up,
  // window first-use is recorded. Snapshot for undo.
  let priorPlanState: PlanStateRecord | null = null;
  let planOutcome: string | undefined;
  if (plan && planState) {
    priorPlanState = JSON.parse(JSON.stringify(planState)) as PlanStateRecord;
    if (planState.state.planType === 'interval') {
      const r = intervalApply(planState.state, nowD, plan.pace);
      planOutcome = r.outcome;
      await planStates.put({ ...planState, state: r.state, updatedAt: nowIso() });
    } else if (planState.state.planType === 'quota') {
      const r = quotaApply(planState.state, plan.pace, nowD);
      planOutcome = r.over ? 'over_quota' : 'success';
      await planStates.put({ ...planState, state: r.state, updatedAt: nowIso() });
    } else if (planState.state.planType === 'window') {
      const r = windowApply(planState.state, nowD);
      planOutcome = r.outcome;
      await planStates.put({ ...planState, state: r.state, updatedAt: nowIso() });
    }
  }

  const ev = await logEvent({
    type: 'usage_logged',
    habitId: habit.id,
    ...(plan ? { planId: plan.id } : {}),
    episodeId: fresh.id,
    occurredAt: nowD.toISOString(),
    localDate: localDate(nowD),
    timezoneOffsetMinutes: tzOffsetMinutes(nowD),
    payload: {
      episodeId: fresh.id,
      source: 'urge_command_center',
      waitedMsBeforeUse: elapsed,
      ...(fresh.targetWaitMs !== undefined ? { targetWaitMsAtUse: fresh.targetWaitMs } : {}),
      ...(fresh.currentChallengeId ? { challengeId: fresh.currentChallengeId } : {}),
      ...(fresh.rescueSessionIds && fresh.rescueSessionIds.length > 0
        ? { rescueSessionIds: fresh.rescueSessionIds } : {}),
      ...(triggers ? { triggers } : {}),
      ...(fresh.intensityStart !== undefined ? { intensityStart: fresh.intensityStart } : {}),
      ...(fresh.intensityEnd !== undefined ? { intensityEnd: fresh.intensityEnd } : {}),
      ...(planOutcome ? { outcome: planOutcome } : {}),
      amountQuality: 'default',
    },
  });
  await linkEventToEpisode(ep.id, ev.id);
  await closeEpisode(ep.id, 'used');
  await logEvent({
    type: 'urge_episode_closed',
    habitId: habit.id,
    ...(plan ? { planId: plan.id } : {}),
    episodeId: ep.id,
    occurredAt: nowIso(),
    localDate: localDate(new Date()),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date()),
    payload: {
      episodeId: ep.id, outcome: 'used',
      totalWaitedMs: elapsed,
      modulesCompleted: ep.modulesCompleted ?? 0,
    },
  });
  trackUrgeResolution('used', ep.modulesCompleted ?? 0, elapsed);
  showUndoToast({
    message: `Logged. Waited ${formatWait(elapsed)} before use.`,
    onUndo: async () => {
      await markEventUndone(ev.id, 'user_undo');
      await updateEpisode(ep.id, { endedAt: undefined, outcome: undefined });
      if (priorPlanState) {
        await planStates.put({ ...priorPlanState, updatedAt: nowIso() });
      }
      toast('Log undone.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    },
  });
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

function formatWait(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function extractEpisodeId(): string | undefined {
  const path = currentRoute().path;
  const match = path.match(/^\/urge\/([^/]+)$/);
  return match?.[1];
}
